import * as React from 'react';
import styled from "styled-components";
import {Title, SectionTitle} from "../styled";
import BackgroundImage from '../images/page-feedback-bcg.png';
import Radio, {Round as RadioRound} from "components/Radio";
import {Formik, Form, FieldProps, Field} from 'formik';
import {transition} from "helpers/css";
import Input, {StyledInput} from "components/Input";
import scrollStyles from "helpers/css/scroll";
import {FeedbackValues, sendFeedback} from "api/landing-feedback";
import {toast} from "react-toastify";

const Content = styled.div`
  width: 100%;
  max-width: 560px;
  padding: 40px 0 40px 50px;
`;

const ImageBox = styled.div`
  flex-grow: 1;
  max-width: 520px;
  max-height: 344px;
  
  img {
    width: 100%;
    object-fit: cover;
  }
`

const RadioWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, max-content);
  gap: 14px;
  margin-top: 31px;
`

const Label = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.01em;
  color: #5C5F65;

`

const RadioBox = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  
  ${Label} {
    margin-left: 10px;
  }
`

const Textarea = styled.textarea`
  box-sizing: border-box;
  padding: 13px 14px;
  font-size: 15px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.01em;
  margin-top: 14px;
  background-color: white;
  width: 100%;
  height: 100px;
  border-radius: 5px;
  
  &:focus {
    box-shadow: 
      0 0 0 1px rgb(50 100 239 / 70%), 
      0 0 0 0 rgb(50 100 239 / 10%);
  }
  
  &::placeholder {
    color: #8A8C93;
  }
  
  ${scrollStyles()};
  
  ${transition()};
`

const ButtonBox = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 160px;
  height: 44px;
  box-sizing: border-box;
  
  font-size: 15px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.01em;
  color: white;
  background: #3264EF;
  border-radius: 5px;
  cursor: pointer;

  ${transition()};
  
  &:hover {
    box-shadow: 10px 10px 20px rgba(50, 100, 239, 0.3);
  }
`

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  background: #F5F9FF;
  border-radius: 20px;
  margin: 0 auto;
  max-width: 1340px;
  opacity: 0;
  transition: opacity 1s;
  padding-right: 10px;
  
  ${RadioRound} {
    box-sizing: border-box;
    width: 20px;
    height: 20px;
    border: 1px solid #D4D6DA;
  }
  
  ${StyledInput} {
    background-color: white;
    margin-top: 18px;
    color: black;

    &::placeholder {
      color: #8A8C93;
    }
  }
  
  @media screen and (max-width: 900px) {
    ${ImageBox} {
      display: none;
    } 
  }
  
  @media screen and (max-width: 650px) {
    ${RadioWrapper} {
      display: flex;
      flex-direction: column;
    } 
    
    ${Content} {
      padding: 20px;
    } 
  }
  
  @media screen and (max-width: 450px) {
    ${Content} {
      padding: 20px 10px;
    } 
    
    ${Button} {
      width: 100%;
    } 
  }
`;

/**
 * render landing page main section
 * @constructor
 */
export default function Feedback(): React.ReactElement {
  const ref = React.createRef<HTMLDivElement>();

  React.useEffect(() => {
    if (ref.current?.style) {
      ref.current.style.opacity = '1'
    }
  }, []);

  const initialValues: FeedbackValues = {
    messageType: 'idea',
    message: '',
    email: '',
  }

  function showMessageStatus(res: any) {
    const isSuccess = res?.status && (res.status >= 200 && res.status <= 299);

    if (isSuccess)
      toast.success('Сообщение отправлено!');
    else
      toast.error('Ошибка! Попробуйте позже')
  }

  function handleSubmit(values: FeedbackValues) {
    sendFeedback(values).then(showMessageStatus)
  }

  return (
    <Container ref={ref}>
      <Content>
        <SectionTitle>связаться с нами</SectionTitle>
        <Title>Обратная связь</Title>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({setFieldValue, values}) => (
            <Form>
              <RadioWrapper>
                <RadioBox>
                  <Field name="messageType">
                    {({field}: FieldProps) => (
                      <Radio
                        {...field}
                        value="idea"
                        defaultChecked={values.messageType === 'idea'}
                        onChange={
                          (e) => setFieldValue(field.name, e.target.value)
                        }
                      />
                    )}
                  </Field>
                  <Label>Предложить идею</Label>
                </RadioBox>
                <RadioBox>
                  <Field name="messageType">
                    {({field}: FieldProps) => (
                      <Radio
                        {...field}
                        value="thanks"
                        onChange={
                          (e) => setFieldValue(field.name, e.target.value)
                        }
                      />
                    )}
                  </Field>
                  <Label>Поблагодарить</Label>
                </RadioBox>
                <RadioBox>
                  <Field name="messageType">
                    {({field}: FieldProps) => (
                      <Radio
                        {...field}
                        value="question"
                        onChange={
                          (e) => setFieldValue(field.name, e.target.value)
                        }
                      />
                    )}
                  </Field>
                  <Label>Задать вопрос</Label>
                </RadioBox>
              </RadioWrapper>
              <Field name="email">
                {({field}: FieldProps) => (
                  <Input
                    type="email"
                    placeholder="E-mail"
                    required
                    onBlur={
                      (e) => setFieldValue(field.name, e.target.value)
                    }
                  />
                )}
              </Field>
              <Field name="message">
                {({field}: FieldProps) => (
                  <Textarea
                    placeholder="Введите текст..."
                    required
                    onBlur={
                      (e) => setFieldValue(field.name, e.target.value)
                    }
                  />
                )}
              </Field>
              <ButtonBox>
                <Button type="submit">Отправить</Button>
              </ButtonBox>
            </Form>
          )}
        </Formik>
      </Content>
      <ImageBox>
        <img src={BackgroundImage} alt=""/>
      </ImageBox>
    </Container>
  );
}
